import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import api from "@/lib/api";
import { useLang } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Printer, Droplets, ArrowLeft, Loader2 } from "lucide-react";

const SIZES = {
  small: { grid: "grid-cols-3 sm:grid-cols-4", pad: "p-2", gap: "gap-2", qr: 52, num: "text-2xl", wrap: "max-w-4xl" },
  a4: { grid: "grid-cols-2 sm:grid-cols-3", pad: "p-3", gap: "gap-4", qr: 80, num: "text-4xl", wrap: "max-w-[210mm]" },
};

export default function CrateTags() {
  const { orderId } = useParams();
  const [sp] = useSearchParams();
  const { t } = useLang();
  const [batches, setBatches] = useState(null);
  const [size, setSize] = useState(() => localStorage.getItem("olive_tag_size") || "small");

  useEffect(() => {
    const ids = orderId
      ? [orderId]
      : (sp.get("ids") || "").split(",").map((s) => s.trim()).filter(Boolean);
    if (!ids.length) { setBatches([]); return; }
    Promise.all(ids.map((id) => api.get(`/public/order/${id}`).then((r) => r.data).catch(() => null)))
      .then((res) => setBatches(res.filter(Boolean)));
  }, [orderId, sp]);

  const pickSize = (s) => { setSize(s); localStorage.setItem("olive_tag_size", s); };

  if (!batches) return <div className="flex min-h-screen items-center justify-center bg-background"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  const cfg = SIZES[size] || SIZES.small;
  const withCrates = batches.filter((b) => (b.order.crates || []).length > 0);
  const totalCrates = withCrates.reduce((a, b) => a + (b.order.crates || []).length, 0);
  const multi = withCrates.length > 1;

  return (
    <div className="min-h-screen bg-secondary/40">
      <div className="no-print sticky top-0 z-10 border-b border-border bg-background/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-4xl flex-wrap items-center justify-between gap-3 px-4 py-3">
          <Button variant="ghost" size="sm" onClick={() => window.close()} data-testid="tags-back-btn"><ArrowLeft className="mr-1 h-4 w-4" /> {t("back")}</Button>
          <div className="text-sm font-medium text-muted-foreground">
            {t("crate_tags")} · {multi ? `${withCrates.length} ${t("batches")}` : withCrates[0]?.order.order_code} · {totalCrates} {t("crates")}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-muted-foreground">{t("tag_size")}:</span>
            {["small", "a4"].map((s) => (
              <button key={s} data-testid={`tag-size-${s}`} onClick={() => pickSize(s)}
                className={`rounded-lg px-2.5 py-1 text-xs font-semibold transition-colors ${size === s ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground"}`}>
                {s === "small" ? t("size_small") : t("size_a4")}
              </button>
            ))}
            <Button data-testid="print-tags-btn" size="sm" onClick={() => window.print()} className="rounded-lg bg-primary hover:bg-olive-hover"><Printer className="mr-1 h-4 w-4" /> {t("print")}</Button>
          </div>
        </div>
      </div>

      <div className={`mx-auto ${cfg.wrap} px-4 py-8`} id="tags">
        {withCrates.length === 0 ? (
          <p className="text-center text-muted-foreground">{t("no_crates_tags")}</p>
        ) : (
          withCrates.map(({ order, mill }) => {
            const trackUrl = `${window.location.origin}/track/${order.order_code}`;
            return (
              <div key={order.id} className="mb-8" data-testid={`tags-batch-${order.order_code}`}>
                {multi && (
                  <div className="no-print mb-3 flex items-center gap-2 text-sm font-semibold">
                    <span className="rounded-md bg-olive-light px-2 py-0.5 font-mono text-olive">{order.order_code}</span>
                    <span>{order.client_name}</span>
                    <span className="text-muted-foreground">· {(order.crates || []).length} {t("crates")}</span>
                  </div>
                )}
                <div className={`grid ${cfg.grid} ${cfg.gap}`}>
                  {(order.crates || []).map((c) => (
                    <div key={c.number} data-testid={`crate-tag-${order.order_code}-${c.number}`}
                      className={`crate-tag flex items-center gap-3 rounded-xl border-2 border-black bg-white ${cfg.pad} text-black`}>
                      <QRCodeSVG value={trackUrl} size={cfg.qr} level="M" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1 text-[10px] font-bold uppercase" style={{ fontFamily: "Outfit, sans-serif" }}>
                          {mill.logo ? <img src={mill.logo} alt="" className="h-4 w-4 shrink-0 rounded object-contain" /> : <Droplets className="h-3 w-3" />} <span className="truncate">{mill.mill_name}</span>
                        </div>
                        <div className="font-mono text-[9px] text-black/60">{order.order_code}</div>
                        <div className={`mt-0.5 ${cfg.num} font-black leading-none`} style={{ fontFamily: "Outfit, sans-serif" }}>#{c.number}</div>
                        <div className="text-[10px] text-black/70">{c.weight} kg</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
